import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { translate } from 'react-i18next';
import { FormSelect } from '../fields/FormSelect';
import { ExtendedCountryList } from '../../utils/countryList';
import { Field, formValueSelector } from 'redux-form';
import { WIZARD_FORM_NAME } from '../../constants/common';
import { connect } from 'react-redux';
import Map from '../elements/Map';
import citiesEurope from '../../utils/citiesEurope.json';
import { TimeZones } from '../../utils/timeZonesList';
import {
  DEFAULT_LAT,
  DEFAULT_LON,
  findCityByBoundaries,
  searchAddress,
  searchLocation,
  searchTimezone,
} from '../../utils/geoLocation';
import LoadingBar from '../elements/LoadingBar';
import SearchBar from '../elements/SearchBar';
import { setField, clearField, toSelectObject } from '../../utils';

const Form = styled.form`
  width: 100%;
  max-width: 100%;
  text-align: center;
  padding: 60px;
  font-size: 14px;
  display: flex;
  justify-content: space-between;
  flex-flow: row wrap;
  gap: 30px;
  box-sizing: border-box;

  label {
    font-weight: bold;
    padding-right: 30px;
  }

  @media (max-width: 900px) {
    display: block;
    margin-bottom: 0;
    padding: 40px 20px;

    label {
      font-size: 18px;
    }
  }
`;

const FieldContainer = styled.div`
  flex: 1;
  margin-bottom: 30px;
`;

const DeviceLocationPage = props => {
  const { handleSubmit, t, reprovisionMode, country } = props;
  const [userMarker, setUserMarker] = useState();
  const [geoLocationLoading, setGeoLocationLoading] = useState(true);
  const [mapPosition, setMapPosition] = useState({
    latitude: DEFAULT_LAT,
    longitude: DEFAULT_LON,
  });

  const countries = ExtendedCountryList.sort((a, b) =>
    a.label.localeCompare(b.label)
  ).map(country => {
    return toSelectObject(country.value, country.label);
  });

  const areas = [toSelectObject('eu', 'EU'), toSelectObject('us', 'US')];

  const cities = citiesEurope
    .filter(city => !!country && city.country === country.value)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(city => toSelectObject(city.name));

  const timeZones = TimeZones.map(timeZone =>
    toSelectObject(timeZone.value, timeZone.label)
  );

  const handleCountryChange = () => {
    clearField(props, 'City');
  };

  const handleCityChange = (e, newValue) => {
    searchAddress(`${newValue.value} ${country.label}`).then(result => {
      if (result) {
        const { lat: latitude, lon: longitude } = result.position;
        buildMarker(latitude, longitude);
      }
    });
  };

  const handleSearchChange = value => {
    if (value) {
      searchAddress(value).then(result => {
        if (result) {
          const { lat: latitude, lon: longitude } = result.position;
          buildMarker(latitude, longitude);
          setTimeout(
            () => fillAddressFields(result.address, result.boundingBox),
            100
          );
        }
      });
    }
  };

  const fillTimeZoneField = data => {
    const timeZoneId = data.TimeZones[0].Id;
    const timeZone = timeZones.find(
      item =>
        item.value === timeZoneId ||
        item.label.search(timeZoneId.split('/')[1]) > -1
    );

    if (timeZone) {
      setField(props, 'TimeZone', timeZone);
    } else {
      clearField(props, 'TimeZone');
    }
  };

  const fillAddressFields = (address, boundingBox) => {
    const { country, countryCode, municipality } = address;
    const locatedCountry = countries.find(
      item => item.value.toLowerCase() === countryCode.toLowerCase()
    );

    // Select eu region as default
    setField(props, 'Area', toSelectObject('eu', 'EU'));

    if (locatedCountry?.value) {
      setField(props, 'Country', locatedCountry);

      // Try to find city name from the list to store concurrent data, otherwise set localname from api result as default
      const matchingCity =
        citiesEurope.find(
          item => item.name.toLowerCase() === municipality?.toLowerCase()
        ) || findCityByBoundaries(boundingBox);
      const locatedCity = toSelectObject(matchingCity?.name || municipality);

      // Wait until the select field reset after country change
      setTimeout(() => {
        locatedCity?.value
          ? setField(props, 'City', locatedCity)
          : clearField(props, 'City');
      }, 1);
    } else {
      // Set the fields as api returns
      setField(props, 'Country', toSelectObject(country));
      setTimeout(
        () => setField(props, 'City', toSelectObject(municipality)),
        1
      );
    }
  };

  const searchTimezoneAndLocation = (lat, lon) => {
    searchTimezone(lat, lon).then(data => data && fillTimeZoneField(data));
    searchLocation(lat, lon).then(
      address => address && fillAddressFields(address, address.boundingBox)
    );
  };

  const buildMarker = (latitude, longitude) => {
    const marker = {
      id: 'user-marker',
      text: '&#9679;', // Large dot symbol
      color: 'red',
      position: [longitude, latitude],
      draggable: true,
      onDrag: ({ lat, lng }) => {
        buildMarker(lat, lng);
        searchTimezoneAndLocation(lat, lng);
      },
    };

    setUserMarker(marker);
    setMapPosition({ latitude, longitude });
  };

  const fetchUserLocation = () => {
    window.navigator.geolocation.getCurrentPosition(
      position => {
        const { latitude, longitude } = position.coords;
        buildMarker(latitude, longitude);
        searchTimezoneAndLocation(latitude, longitude);
        setGeoLocationLoading(false);
      },
      () => {
        buildMarker(DEFAULT_LAT, DEFAULT_LON);
        setGeoLocationLoading(false);
      },
      {
        enableHighAccuracy: true,
      }
    );
  };

  useEffect(fetchUserLocation, []);

  useEffect(() => {
    setField(props, 'Latitude', mapPosition.latitude);
    setField(props, 'Longitude', mapPosition.longitude);
  }, [mapPosition, props]);

  return (
    <>
      {geoLocationLoading ? (
        <LoadingBar />
      ) : (
        <>
          <SearchBar t={t} onChange={handleSearchChange} />
          <Map
            htmlMarkers={userMarker ? [userMarker] : []}
            latitude={mapPosition.latitude}
            longitude={mapPosition.longitude}
            zoom={10}
          />
        </>
      )}
      <Form onSubmit={handleSubmit}>
        {!reprovisionMode && (
          <FieldContainer>
            <label>{t('device_country.area')}</label>
            <Field name="Area" component={FormSelect} options={areas} />
          </FieldContainer>
        )}
        <FieldContainer>
          <label>{t('device_country.title')}</label>
          <Field
            name="Country"
            component={FormSelect}
            options={countries}
            onChange={handleCountryChange}
          />
        </FieldContainer>
        <FieldContainer>
          <label>{t('device_city.title')}</label>
          <Field
            name="City"
            component={FormSelect}
            options={cities}
            onChange={handleCityChange}
          />
        </FieldContainer>
        <FieldContainer>
          <label>{t('device_timeZone.title')}</label>
          <Field name="TimeZone" component={FormSelect} options={timeZones} />
        </FieldContainer>
      </Form>
    </>
  );
};

const selector = formValueSelector(WIZARD_FORM_NAME);
const mapStateToProps = state => ({
  reprovisionMode: selector(state, 'ReprovisionMode'),
  country: selector(state, 'Country'),
});

export default connect(mapStateToProps)(translate()(DeviceLocationPage));
